import { isNhlRegularSeasonMarket } from '@sportex100/shared/utils/nhlMarketFilter.ts';
import { ArrowDown, ArrowUp, Minus, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildWorldCupTeamSections,
  isWorldCupCompetition,
  WORLD_CUP_ALL_GROUPS,
  type WorldCupGroupFilterValue,
  type WorldCupStatusFilterValue,
} from "@sportex100/shared/utils/worldCupTeamFilter.ts";
import {
  buildPlayoffPictureSections,
  buildStandingsMetricPills,
} from "@sportex100/shared/utils/playoffPicture.ts";
import {
  filterMarketSections,
  isStructureFilterMarket,
  matchesMarketStructure,
  nextMarketConferenceSelection,
  marketStructureOptions,
  type MarketConferenceFilter,
  type MarketDivisionFilter,
} from "@sportex100/shared/utils/marketStructureFilter.ts";
import { SeasonStanding, OrderBookLadder } from "../types/market";
import { MarketCell } from "./MarketCell";
import { OrderBookLadderView } from "./OrderBookLadderView";
import { TeamCodeBadge } from "./ui/TeamCodeBadge";
import { TEAM_IDENTITY_BADGE_SIZE, TEAM_IDENTITY_NAME_CLASS } from "./ui/teamIdentityScale";
import { useOrderBookLadder } from "../hooks/useOrderBookLadder";
import { OrderbookMessage } from "../hooks/useOrderbookStream";
import { SegmentedFilter, StatusMessage } from "./ui";
import { cn } from "../lib/utils";
import { isLadderAlignedWithTopOfBook } from "./marketDepth";
import { useLocalization } from "../lib/i18n";
import { getWorldCupFlagCountryCode } from "@sportex100/shared/utils/worldCupFlags";
import { isSoccerTradingCompetition } from "@sportex100/shared/utils/competitionIdentity.ts";
import {
  buildRecordPills,
  groupRegularSeasonTeams,
  isMarketTeamEliminated,
  isRegularSeasonPhase,
  resolveMarketRowExpansion,
} from "../lib/regularSeasonRecord";
import {
  localizeSeasonPhase,
  localizeStructureLabel,
  RegularSeasonMarketFilter,
  RegularSeasonRecordPills,
  RegularSeasonStandingsMetricPills,
  RegularSeasonStructureHeadings,
  WildCardCutLine,
  regularSeasonTopLevelKind,
  standingsMetricsAccessibilityLabel,
  type RegularSeasonTopLevelKind,
} from "./RegularSeasonMarket";

type MarketTableProps = {
  standings: SeasonStanding[];
  onSelectOrder?: (payload: { teamCode: string; side: "BUY" | "SELL"; price?: number }) => void;
  competitionCode?: string;
  seasonCode?: string;
  poolCode?: string;
  orderbookMessage?: OrderbookMessage | null;
};

type MarketRowProps = {
  row: SeasonStanding;
  onSelectOrder?: (payload: { teamCode: string; side: "BUY" | "SELL"; price?: number }) => void;
  competitionCode: string;
  seasonCode: string;
  poolCode: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  orderbookMessage?: OrderbookMessage | null;
};

type MarketSection = {
  key: string;
  title: string;
  data: SeasonStanding[];
  topLevelTitle?: string;
  topLevelKind?: RegularSeasonTopLevelKind;
  isRegularSeasonStructure?: boolean;
  showCutLineAfter?: boolean;
};

type MarketFilterValue = "ALL" | "ACTIVE" | "ELIMINATED";

type MarketTranslator = ReturnType<typeof useLocalization>["t"];

type WorldCupMarketFilterProps = {
  selectedGroup: WorldCupGroupFilterValue;
  statusFilter: WorldCupStatusFilterValue;
  searchQuery: string;
  visibleCount: number;
  totalCount: number;
  onGroupChange: (value: WorldCupGroupFilterValue) => void;
  onStatusChange: (value: WorldCupStatusFilterValue) => void;
  onSearchChange: (value: string) => void;
  t: MarketTranslator;
};

const worldCupStatusOptions: Array<{
  value: WorldCupStatusFilterValue;
  labelKey: Parameters<MarketTranslator>[0];
  accessibilityKey: Parameters<MarketTranslator>[0];
}> = [
  {
    value: "ALL",
    labelKey: "market.worldCup.status.all",
    accessibilityKey: "market.worldCup.status.allAccessibility",
  },
  {
    value: "LIVE",
    labelKey: "market.worldCup.status.live",
    accessibilityKey: "market.worldCup.status.liveAccessibility",
  },
  {
    value: "ELIMINATED",
    labelKey: "market.worldCup.status.out",
    accessibilityKey: "market.worldCup.status.outAccessibility",
  },
];

function normalizeTeamState(state: string | null | undefined) {
  return (state ?? "").trim().toUpperCase();
}

function isChampionState(state: string | null | undefined) {
  const normalized = normalizeTeamState(state);
  return normalized === "CHAMPION" || normalized === "CHAMP";
}

function isActiveMarketState(state: string | null | undefined) {
  return new Set(["ACTIVE", "PLAYING"]).has(normalizeTeamState(state));
}

function getRoundText(row: SeasonStanding) {
  return row.current_round_name ?? row.current_round?.replace(/_/g, " ") ?? null;
}

function getLastTradeTone(trend: SeasonStanding["last_trade_trend"]) {
  if (trend === "UP") return "text-success";
  if (trend === "DOWN") return "text-danger";
  return "text-foreground-muted";
}

function LastTradeGlyph({ trend }: { trend: SeasonStanding["last_trade_trend"] }) {
  const className = cn("ml-0.5 inline h-3 w-3 align-[-0.12em]", getLastTradeTone(trend));
  if (trend === "UP") return <ArrowUp className={className} aria-hidden="true" />;
  if (trend === "DOWN") return <ArrowDown className={className} aria-hidden="true" />;
  if (trend === "FLAT") return <Minus className={className} aria-hidden="true" />;
  return null;
}

function WorldCupMarketFilter({
  selectedGroup,
  statusFilter,
  searchQuery,
  visibleCount,
  totalCount,
  onGroupChange,
  onStatusChange,
  onSearchChange,
  t,
}: WorldCupMarketFilterProps) {
  const groupOptions: WorldCupGroupFilterValue[] = [
    "ALL",
    ...WORLD_CUP_ALL_GROUPS.map((group) => group),
  ];

  return (
    <section className="rounded-2xl border border-border-subtle bg-background-card p-3 shadow-card md:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-faint"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t("market.worldCup.searchPlaceholder")}
            className="h-11 w-full rounded-xl border border-border-subtle bg-background-raised pl-9 pr-16 text-sm text-foreground outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/30"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            <span className="rounded-full bg-background px-2 py-0.5 text-[11px] font-semibold tabular-nums text-foreground-muted">
              {visibleCount}/{totalCount}
            </span>
            {searchQuery.trim().length > 0 && (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-border-subtle text-foreground-muted transition hover:border-brand hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                aria-label={t("market.worldCup.clearSearch")}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <SegmentedFilter
          value={statusFilter}
          onChange={onStatusChange}
          ariaLabel="Team status filter"
          className="w-full lg:w-[310px]"
          options={worldCupStatusOptions.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
            activeTone:
              option.value === "LIVE" ? "buy" : option.value === "ELIMINATED" ? "sell" : "accent",
          }))}
        />
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle">
          {t("market.worldCup.groups")}
        </span>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {groupOptions.map((group) => {
            const isSelected = selectedGroup === group;
            return (
              <button
                key={group}
                type="button"
                onClick={() => onGroupChange(group)}
                className={cn(
                  "min-h-8 min-w-9 shrink-0 rounded-[11px] px-2.5 py-1.5 font-display text-sm font-bold leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                  isSelected
                    ? "bg-background-card text-brand shadow-[0_1px_0_rgba(255,255,255,0.08)]"
                    : "text-foreground-faint hover:text-foreground",
                )}
                aria-pressed={isSelected}
              >
                {group === "ALL" ? t("market.worldCup.groupAll") : group}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MarketRow({
  row,
  onSelectOrder,
  competitionCode,
  seasonCode,
  poolCode,
  isExpanded,
  onToggleExpand,
  orderbookMessage,
}: MarketRowProps) {
  const { t } = useLocalization();
  const expansion = resolveMarketRowExpansion({ requestedExpanded: isExpanded, team: row });
  const updateCallbackRef = useRef<((ladder: OrderBookLadder) => void) | null>(null);

  const handleWebSocketUpdate = useCallback((callback: (ladder: OrderBookLadder) => void) => {
    updateCallbackRef.current = callback;
  }, []);

  useEffect(() => {
    if (
      orderbookMessage &&
      expansion.effectiveExpanded &&
      orderbookMessage.season_team_id === row.season_team_id &&
      orderbookMessage.full_ladder &&
      updateCallbackRef.current
    ) {
      updateCallbackRef.current(orderbookMessage.full_ladder);
    }
  }, [orderbookMessage, expansion.effectiveExpanded, row.season_team_id]);

  const { ladder, isLoading, isError, reload } = useOrderBookLadder({
    competitionCode,
    seasonCode,
    teamCode: row.team_code,
    poolCode,
    enabled: expansion.hookEnabled,
    onWebSocketUpdate: expansion.hookEnabled ? handleWebSocketUpdate : undefined,
  });

  useEffect(() => {
    if (!expansion.effectiveExpanded) {
      return;
    }
    if (
      !orderbookMessage?.full_ladder ||
      orderbookMessage.season_team_id !== row.season_team_id
    ) {
      return;
    }

    const ladderIsAligned = isLadderAlignedWithTopOfBook(
      orderbookMessage.full_ladder.bid ?? [],
      orderbookMessage.full_ladder.ask ?? [],
      {
        topBidOrderId: row.top_bid_order_id,
        topAskOrderId: row.top_ask_order_id,
        hasTopBid: row.top_bid_order_id != null,
        hasTopAsk: row.top_ask_order_id != null,
      },
    );

    if (!ladderIsAligned) {
      void reload();
    }
  }, [
    expansion.effectiveExpanded,
    orderbookMessage,
    reload,
    row.season_team_id,
    row.top_ask_order_id,
    row.top_bid_order_id,
  ]);

  const panelId = `ladder-${row.season_team_id}`;
  const isEliminated = isMarketTeamEliminated(row);
  const isRegularSeason = isRegularSeasonPhase(row.season_phase);
  const worldCupFlagCountryCode =
    isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(row.team_code) : null;
  const roundText = getRoundText(row);
  const floorText = `${t("market.floor.short")} ${Math.round(row.market_floor_price)}`;
  const valueText =
    !isRegularSeason && roundText ? `${floorText} • ${roundText.toUpperCase()}` : floorText;
  const recordPills = buildRecordPills(
    competitionCode,
    row.regular_season_record,
    row.season_phase,
  );
  const standingsMetricPills = buildStandingsMetricPills(competitionCode, row);
  const standingsMetricDescriptionId =
    standingsMetricPills.length > 0
      ? `market-standings-metrics-${row.season_team_id}`
      : undefined;
  const streakEntries =
    isRegularSeason
      ? []
      : row.current_round_streak && row.current_round_streak.length > 0
      ? row.current_round_streak
      : row.current_round_wins + row.current_round_looses > 0
        ? [...Array(row.current_round_wins).fill("W"), ...Array(row.current_round_looses).fill("L")]
        : [];
  const mobilePanelId = `${panelId}-mobile`;
  const tabletPanelId = `${panelId}-tablet`;

  const renderBookPanel = (variant: "default" | "tabletCard", id: string) => (
    <div
      id={id}
      className={cn(
        "marketSeamlessBookPanel relative z-10 w-full overflow-hidden",
        variant === "default" ? "mt-3" : "mt-0",
      )}
    >
      <MarketCell
        teamCode={row.team_code}
        teamState={row.team_state}
        topBidPrice={row.top_bid_price}
        topBidQuantity={row.top_bid_quantity}
        topBidTraderCode={row.top_bid_trader_code}
        topBidParticipantType={row.top_bid_participant_type}
        topBidParticipantDisplayName={row.top_bid_participant_display_name}
        topBidOrderId={row.top_bid_order_id}
        topAskPrice={row.top_ask_price}
        topAskQuantity={row.top_ask_quantity}
        topAskTraderCode={row.top_ask_trader_code}
        topAskParticipantType={row.top_ask_participant_type}
        topAskParticipantDisplayName={row.top_ask_participant_display_name}
        topAskOrderId={row.top_ask_order_id}
        marketFloorPrice={row.market_floor_price}
        isExpanded={expansion.effectiveExpanded}
        variant={variant}
        onSelectOrder={onSelectOrder}
      />
      {expansion.panelVisible && (
        <OrderBookLadderView
          ladder={ladder}
          isLoading={isLoading}
          isError={isError}
          teamCode={row.team_code}
          topBidOrderId={row.top_bid_order_id}
          topAskOrderId={row.top_ask_order_id}
          showSideLabelColumns={variant === "tabletCard"}
        />
      )}
    </div>
  );

  return (
    <article
      className={cn(
        "marketMobileCard relative mb-3 overflow-hidden rounded-xl border border-border bg-background-card p-3 shadow-[0_12px_28px_rgba(0,0,0,0.28)] transition-all duration-200 ease-out hover:border-brand/40 md:rounded-2xl md:px-4 md:py-3",
        isEliminated && "opacity-60",
        expansion.effectiveExpanded &&
          "border-brand/60 shadow-[0_4px_16px_rgba(0,0,0,0.2)]",
      )}
      aria-expanded={expansion.ariaExpanded}
    >
      {standingsMetricDescriptionId ? (
        <span id={standingsMetricDescriptionId} className="sr-only">
          {standingsMetricsAccessibilityLabel(standingsMetricPills, t)}
        </span>
      ) : null}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <span className="absolute -left-7 -top-8 h-[118px] w-[96%] rotate-[-16deg] rounded-full bg-[linear-gradient(110deg,rgba(255,255,255,0),rgba(255,255,255,0.045),rgba(255,255,255,0))] opacity-75" />
        <span className="absolute -right-10 top-10 h-[94px] w-[92%] rotate-[-16deg] rounded-full bg-[linear-gradient(110deg,rgba(255,255,255,0),rgba(255,255,255,0.032),rgba(255,255,255,0))] opacity-65" />
        <span className="absolute -bottom-5 left-4 h-[76px] w-[70%] rotate-[12deg] rounded-full bg-[linear-gradient(110deg,rgba(255,255,255,0),rgba(255,255,255,0.024),rgba(255,255,255,0))] opacity-50" />
      </div>

      <div className="relative z-10 md:hidden">
        <button
          type="button"
          onClick={onToggleExpand}
          disabled={expansion.isTradingClosed}
          aria-label={
            expansion.controlLabel === "collapse"
              ? t("market.orderBook.collapse")
              : t("market.orderBook.expand")
          }
          aria-expanded={expansion.ariaExpanded}
          aria-controls={mobilePanelId}
          aria-describedby={standingsMetricDescriptionId}
          className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-3 text-left disabled:cursor-default"
        >
          <TeamCodeBadge
            code={row.team_code}
            size={TEAM_IDENTITY_BADGE_SIZE.market.mobileBadge}
            variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
            flagCountryCode={worldCupFlagCountryCode}
            primaryColor={row.team_primary_color}
            secondaryColor={row.team_secondary_color}
          />

          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-2">
            <div className="min-w-0">
              <p className={TEAM_IDENTITY_NAME_CLASS.marketMobile}>
                {row.team_name}
              </p>
              <p className="mt-2 truncate font-mono text-[13px] font-semibold text-foreground-subtle">
                {valueText}
              </p>
              {recordPills.length > 0 || standingsMetricPills.length > 0 ? (
                <div className="mt-3 flex min-w-0 flex-wrap items-center gap-1">
                  <RegularSeasonRecordPills teamCode={row.team_code} recordPills={recordPills} />
                  <RegularSeasonStandingsMetricPills
                    teamCode={row.team_code}
                    metrics={standingsMetricPills}
                  />
                </div>
              ) : streakEntries.length > 0 ? (
                <div className="mt-3 flex items-center gap-1">
                  {streakEntries.map((entry, index) => {
                    const token = entry.toUpperCase().startsWith("W") ? "W" : "L";
                    return (
                      <span
                        key={`${row.team_code}-market-streak-${index}`}
                        className={cn(
                          "inline-flex h-5 min-w-5 items-center justify-center rounded border px-1 font-mono text-[10px] font-semibold",
                          token === "W"
                            ? "border-success/35 bg-success/10 text-success"
                            : "border-danger/35 bg-danger/10 text-danger",
                        )}
                      >
                        {token}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 flex-col items-end gap-2">
              <span className="rounded-full bg-background px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.08em] text-foreground-muted">
                {isRegularSeason
                  ? localizeSeasonPhase(row.season_phase, t)
                  : <>VS {row.opponent_team_code ?? "TBD"}</>}
              </span>
              {row.last_trade_price != null ? (
                <span className="rounded-full bg-background px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground-muted">
                  {t("market.last.short")} {row.last_trade_price.toFixed(0)}
                  <LastTradeGlyph trend={row.last_trade_trend} />
                </span>
              ) : null}
            </div>
          </div>
        </button>

        {!expansion.isTradingClosed ? renderBookPanel("default", mobilePanelId) : null}
      </div>

      <div className="marketTabletCard relative z-10 hidden md:flex md:flex-col md:gap-2 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(26rem,0.95fr)] lg:items-start">
        <button
          type="button"
          className="marketTabletIdentity grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-transparent px-0.5 py-0 text-left transition disabled:cursor-default disabled:opacity-80 focus-visible:outline-none md:grid-cols-[auto_minmax(0,1fr)_auto]"
          onClick={onToggleExpand}
          disabled={expansion.isTradingClosed}
          aria-label={
            expansion.controlLabel === "collapse"
              ? t("market.orderBook.collapse")
              : t("market.orderBook.expand")
          }
          aria-expanded={expansion.ariaExpanded}
          aria-controls={tabletPanelId}
          aria-describedby={standingsMetricDescriptionId}
        >
          <TeamCodeBadge
            code={row.team_code}
            size={TEAM_IDENTITY_BADGE_SIZE.market.tabletBadge}
            variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
            flagCountryCode={worldCupFlagCountryCode}
            primaryColor={row.team_primary_color}
            secondaryColor={row.team_secondary_color}
          />

          <div className="min-w-0">
            <p className={TEAM_IDENTITY_NAME_CLASS.marketTablet}>
              {row.team_name}
            </p>
            <div className="marketTabletMetaLine mt-1.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <p className="min-w-0 truncate font-mono text-[14px] font-semibold uppercase text-foreground-subtle">
                {valueText}
              </p>
              {recordPills.length > 0 || standingsMetricPills.length > 0 ? (
                <>
                  <RegularSeasonRecordPills teamCode={row.team_code} recordPills={recordPills} />
                  <RegularSeasonStandingsMetricPills
                    teamCode={row.team_code}
                    metrics={standingsMetricPills}
                  />
                </>
              ) : streakEntries.length > 0 ? (
                <div className="flex items-center gap-1">
                  {streakEntries.map((entry, index) => {
                    const token = entry.toUpperCase().startsWith("W") ? "W" : "L";
                    return (
                      <span
                        key={`${row.team_code}-tablet-market-streak-${index}`}
                        className={cn(
                          "inline-flex h-5 min-w-5 items-center justify-center rounded border px-1 font-mono text-[10px] font-semibold",
                          token === "W"
                            ? "border-success/35 bg-success/10 text-success"
                            : "border-danger/35 bg-danger/10 text-danger",
                        )}
                      >
                        {token}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          <div className="marketTabletActionRail flex w-[8.75rem] shrink-0 items-center justify-end gap-2">
            <div className="flex min-w-0 flex-col items-end gap-1.5">
              <span className="rounded-full bg-background px-3 py-1 font-mono text-[12px] uppercase tracking-[0.08em] text-foreground-muted">
                  {isRegularSeason
                    ? localizeSeasonPhase(row.season_phase, t)
                    : <>VS {row.opponent_team_code ?? "TBD"}</>}
              </span>
              {row.last_trade_price != null ? (
                <span className="rounded-full bg-background px-3 py-1 font-mono text-[12px] font-semibold uppercase tracking-[0.06em] text-foreground-muted">
                  {t("market.last.short")} {row.last_trade_price.toFixed(0)}
                  <LastTradeGlyph trend={row.last_trade_trend} />
                </span>
              ) : null}
            </div>
            {!expansion.isTradingClosed ? (
              <span
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border-subtle/60 text-foreground-muted transition-all duration-200",
                  expansion.effectiveExpanded && "rotate-90 border-brand text-brand",
                )}
                aria-hidden="true"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            ) : null}
          </div>
        </button>

        {!expansion.isTradingClosed ? (
          <div className="marketTabletQuotePanel flex w-full min-w-0 items-stretch rounded-2xl border border-border-subtle/50 bg-background-raised/70 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]">
            {renderBookPanel("tabletCard", tabletPanelId)}
          </div>
        ) : null}
      </div>
    </article>
  );
}

export function MarketTable({
  standings,
  onSelectOrder,
  competitionCode,
  seasonCode,
  poolCode,
  orderbookMessage,
}: MarketTableProps) {
  const { locale, t } = useLocalization();
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);
  const [marketFilter, setMarketFilter] = useState<MarketFilterValue>("ALL");
  const [worldCupGroupFilter, setWorldCupGroupFilter] =
    useState<WorldCupGroupFilterValue>("ALL");
  const [worldCupStatusFilter, setWorldCupStatusFilter] =
    useState<WorldCupStatusFilterValue>("ALL");
  const [worldCupSearchQuery, setWorldCupSearchQuery] = useState("");
  const [regularSeasonSearchQuery, setRegularSeasonSearchQuery] = useState("");
  const [structureConference, setStructureConference] = useState<MarketConferenceFilter>("ALL");
  const [structureDivision, setStructureDivision] = useState<MarketDivisionFilter>("ALL");
  const isWorldCupMarket = isWorldCupCompetition(competitionCode);
  const seasonPhase = standings[0]?.season_phase;
  const regularSeasonStatusFilter = seasonPhase === "REGULAR_SEASON" ? "ALL" : marketFilter;
  const isStructureMarket = isStructureFilterMarket(competitionCode, seasonPhase);
  const isNhlMarket = isNhlRegularSeasonMarket(competitionCode, seasonPhase);
  const structureOptions = marketStructureOptions(standings, structureConference);
  const isRegularSeasonMarket = standings.some((team) =>
    isRegularSeasonPhase(team.season_phase),
  );

  useEffect(() => {
    setWorldCupGroupFilter("ALL");
    setWorldCupStatusFilter("ALL");
    setWorldCupSearchQuery("");
    setRegularSeasonSearchQuery("");
    setStructureConference("ALL");
    setStructureDivision("ALL");
    setMarketFilter("ALL");
    setExpandedTeamId(null);
  }, [competitionCode, poolCode, seasonCode]);

  useEffect(() => {
    if (seasonPhase === "REGULAR_SEASON") setMarketFilter("ALL");
  }, [seasonPhase]);

  const playoffPictureSections = useMemo(() => {
    if (isWorldCupMarket || seasonPhase !== "REGULAR_SEASON") {
      return null;
    }
    const fullPicture = buildPlayoffPictureSections(standings, {
      competitionCode,
      searchQuery: regularSeasonSearchQuery,
      statusFilter: regularSeasonStatusFilter,
    });
    return fullPicture && isStructureMarket
      ? filterMarketSections(fullPicture, structureConference, structureDivision)
      : fullPicture;
  }, [
    competitionCode,
    isWorldCupMarket,
    isStructureMarket,
    regularSeasonStatusFilter,
    structureConference,
    structureDivision,
    regularSeasonSearchQuery,
    seasonPhase,
    standings,
  ]);

  const sections = useMemo<MarketSection[]>(() => {
    if (isWorldCupMarket) {
      return buildWorldCupTeamSections(standings, {
        selectedGroup: worldCupGroupFilter,
        statusFilter: worldCupStatusFilter,
        searchQuery: worldCupSearchQuery,
      }).map((section) => {
        if (section.group === "ELIMINATED") {
          return {
            key: "world-cup-eliminated",
            title: t("market.sections.eliminatedTeams"),
            data: section.data,
          };
        }
        if (section.group === "OTHER") {
          return {
            key: "world-cup-other",
            title: t("market.sections.otherTeams"),
            data: section.data,
          };
        }
        return {
          key: `world-cup-${section.group}`,
          title: t("market.sections.group", { group: section.group }),
          data: section.data,
        };
      });
    }

    if (isRegularSeasonMarket) {
      const topLevelKind = regularSeasonTopLevelKind(competitionCode);
      if (seasonPhase === "REGULAR_SEASON" && playoffPictureSections !== null) {
        return playoffPictureSections.map((section, index) => {
          const isFirstTopLevelSection =
            index === 0 ||
            playoffPictureSections[index - 1]?.topLevelCode !== section.topLevelCode;
          const title =
            section.kind === "DIVISION"
              ? section.divisionCode
                ? localizeStructureLabel(section.divisionCode, t)
                : t("market.playoffPicture.divisionLeaders")
              : section.kind === "WILD_CARD"
                ? t("market.playoffPicture.wildCard")
                : t("market.playoffPicture.outside");

          return {
            key: section.key,
            title,
            topLevelTitle: isFirstTopLevelSection
              ? localizeStructureLabel(section.topLevelCode, t)
              : undefined,
            topLevelKind,
            isRegularSeasonStructure: true,
            showCutLineAfter: section.showCutLineAfter,
            data: section.data,
          };
        });
      }

      const structureRows = isStructureMarket
        ? standings.filter((team) => matchesMarketStructure(team, structureConference, structureDivision))
        : standings;
      return groupRegularSeasonTeams(structureRows, {
        searchQuery: regularSeasonSearchQuery,
        statusFilter: regularSeasonStatusFilter,
      }).map((group) => ({
        key: group.key,
        title: localizeStructureLabel(group.divisionCode, t),
        topLevelTitle: group.isFirstConferenceGroup
          ? localizeStructureLabel(group.conferenceCode, t)
          : undefined,
        topLevelKind,
        isRegularSeasonStructure: true,
        data: group.teams,
      }));
    }

    const championTeams = standings.filter((team) => isChampionState(team.team_state));
    const activeTeams = standings.filter(
      (team) => isActiveMarketState(team.team_state) && !isMarketTeamEliminated(team),
    );
    const eliminatedTeams = standings.filter((team) => isMarketTeamEliminated(team));
    const marketSections: MarketSection[] = [];

    if (marketFilter === "ALL" && championTeams.length > 0) {
      marketSections.push({
        key: "champion",
        title: t("market.sections.champion"),
        data: championTeams,
      });
    }
    if ((marketFilter === "ALL" || marketFilter === "ACTIVE") && activeTeams.length > 0) {
      marketSections.push({
        key: "active",
        title: t("market.sections.activeTeams"),
        data: activeTeams,
      });
    }
    if ((marketFilter === "ALL" || marketFilter === "ELIMINATED") && eliminatedTeams.length > 0) {
      marketSections.push({
        key: "eliminated",
        title: t("market.sections.eliminatedTeams"),
        data: eliminatedTeams,
      });
    }

    return marketSections;
  }, [
    competitionCode,
    isWorldCupMarket,
    isRegularSeasonMarket,
    isStructureMarket,
    marketFilter,
    regularSeasonStatusFilter,
    structureConference,
    structureDivision,
    playoffPictureSections,
    regularSeasonSearchQuery,
    seasonPhase,
    standings,
    t,
    worldCupGroupFilter,
    worldCupSearchQuery,
    worldCupStatusFilter,
  ]);

  const visibleWorldCupTeamCount = isWorldCupMarket
    ? sections.reduce((count, section) => count + section.data.length, 0)
    : standings.length;
  const visibleRegularSeasonTeamCount = isRegularSeasonMarket
    ? sections.reduce((count, section) => count + section.data.length, 0)
    : standings.length;
  const playoffPictureUnavailable =
    !isWorldCupMarket &&
    seasonPhase === "REGULAR_SEASON" &&
    playoffPictureSections === null;
  const playoffPictureSnapshot =
    seasonPhase === "REGULAR_SEASON" && playoffPictureSections !== null
      ? standings[0]
      : null;
  const playoffPictureUpdatedAt = useMemo(() => {
    const value = playoffPictureSnapshot?.standings_updated_at;
    if (!value) return null;
    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) return null;
    return timestamp.toLocaleString(locale);
  }, [locale, playoffPictureSnapshot?.standings_updated_at]);

  useEffect(() => {
    if (
      expandedTeamId != null &&
      !sections.some((section) =>
        section.data.some((row) => row.season_team_id === expandedTeamId),
      )
    ) {
      setExpandedTeamId(null);
    }
  }, [expandedTeamId, sections]);

  if (!standings.length) {
    return <StatusMessage>{t("market.empty.defaultHint")}</StatusMessage>;
  }

  if (!competitionCode || !seasonCode || !poolCode) {
    return <StatusMessage>{t("market.noPool.message")}</StatusMessage>;
  }

  const handleToggleExpand = (teamId: number) => {
    setExpandedTeamId((prev) => (prev === teamId ? null : teamId));
  };

  return (
    <div className="flex flex-col gap-6">
      {isWorldCupMarket && (
        <WorldCupMarketFilter
          selectedGroup={worldCupGroupFilter}
          statusFilter={worldCupStatusFilter}
          searchQuery={worldCupSearchQuery}
          visibleCount={visibleWorldCupTeamCount}
          totalCount={standings.length}
          onGroupChange={setWorldCupGroupFilter}
          onStatusChange={setWorldCupStatusFilter}
          onSearchChange={setWorldCupSearchQuery}
          t={t}
        />
      )}

      {isRegularSeasonMarket && !isWorldCupMarket ? (
        <div className="space-y-2">
          <RegularSeasonMarketFilter
            statusFilter={marketFilter}
            showStatusFilter={seasonPhase !== "REGULAR_SEASON"}
            searchQuery={regularSeasonSearchQuery}
            visibleCount={visibleRegularSeasonTeamCount}
            totalCount={standings.length}
            onStatusChange={setMarketFilter}
            onSearchChange={setRegularSeasonSearchQuery}
            structure={isStructureMarket ? {
              conference: structureConference,
              division: structureDivision,
              conferences: structureOptions.conferences,
              divisions: structureOptions.divisions,
              onConferenceChange: (code) => {
                const next = nextMarketConferenceSelection(code, structureDivision);
                setStructureConference(next.conference);
                setStructureDivision(next.division);
              },
              onDivisionChange: setStructureDivision,
            } : undefined}
          />
          {playoffPictureUnavailable && !isNhlMarket ? (
            <p className="px-1 text-xs text-foreground-muted">
              {t("market.playoffPicture.unavailable")}
            </p>
          ) : null}
          {playoffPictureUpdatedAt && !isNhlMarket ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 font-mono text-[10px] uppercase tracking-[0.08em] text-foreground-faint">
              <span>
                {t("market.playoffPicture.updatedAt", {
                  timestamp: playoffPictureUpdatedAt,
                })}
              </span>
              {playoffPictureSnapshot?.standings_delayed ? (
                <span className="text-brand">{t("market.playoffPicture.delayed")}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!isWorldCupMarket && !isRegularSeasonMarket && (
        <SegmentedFilter
          value={marketFilter}
          onChange={setMarketFilter}
          ariaLabel="Market team status filter"
          className="w-full"
          options={[
            { value: "ALL", label: t("market.filters.all") },
            { value: "ACTIVE", label: t("market.filters.active") },
            { value: "ELIMINATED", label: t("market.filters.eliminated"), activeTone: "sell" },
          ]}
        />
      )}

      {sections.length === 0 && (isWorldCupMarket || isRegularSeasonMarket) ? (
        <StatusMessage>
          <span className="block font-semibold text-foreground">
            {t("market.empty.noTeams")}
          </span>
          {isWorldCupMarket ? (
            <span className="mt-1 block">{t("market.empty.worldCupHint")}</span>
          ) : null}
          {isStructureMarket ? (
            <span className="mt-1 block">{t("market.structureFilter.emptyHint")}</span>
          ) : null}
        </StatusMessage>
      ) : (
        sections.map((section) => (
          <section key={section.key} className="space-y-3">
            {section.isRegularSeasonStructure ? (
              <RegularSeasonStructureHeadings
                topLevelTitle={section.topLevelTitle}
                topLevelKind={section.topLevelKind ?? "conference"}
                divisionTitle={section.title}
              />
            ) : (
              <h2 className="border-b border-border-subtle/60 pb-2 font-display text-2xl font-bold uppercase tracking-[0.04em] text-foreground md:rounded-lg md:bg-background-elevated md:px-4 md:py-2 md:text-base md:font-semibold md:tracking-wider md:text-foreground-secondary">
                {section.title}
              </h2>
            )}
            {section.isRegularSeasonStructure ? (
              <div className="regularSeasonDesktopColumns hidden grid-cols-[minmax(0,1fr)_minmax(26rem,0.95fr)] px-4 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground-faint lg:grid">
                <span>Contract</span>
                <span>Order book</span>
              </div>
            ) : null}
            {section.data.map((row) => (
              <MarketRow
                key={row.season_team_id}
                row={row}
                onSelectOrder={onSelectOrder}
                competitionCode={competitionCode}
                seasonCode={seasonCode}
                poolCode={poolCode}
                isExpanded={expandedTeamId === row.season_team_id}
                onToggleExpand={() => handleToggleExpand(row.season_team_id)}
                orderbookMessage={orderbookMessage}
              />
            ))}
            {section.showCutLineAfter ? <WildCardCutLine /> : null}
          </section>
        ))
      )}
    </div>
  );
}

export default MarketTable;
